import { Box, FormControl, FormLabel } from '@chakra-ui/react'
import { useId, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Select, { type SingleValue } from 'react-select'
import { type Nationality } from '../../../../model/patient'
import {
  type CountryOption,
  getCountryOptions,
  normalizeNationality
} from '../../../../utils/countryUtils'

interface Props {
  label: string
  value: Nationality | string | undefined
  onChange: (value: Nationality) => void
  onBlur?: () => void
}

export default function CountrySelect({
  label,
  value,
  onChange,
  onBlur
}: Props) {
  const { formatMessage, locale } = useIntl()
  const inputId = useId()
  const options = useMemo(() => getCountryOptions(locale), [locale])
  const normalizedValue = normalizeNationality(value)
  const selectedOption =
    options.find((option) => option.value === normalizedValue) ?? null

  const handleChange = (option: SingleValue<CountryOption>) => {
    onChange(option?.value ?? null)
  }

  return (
    <FormControl>
      <FormLabel htmlFor={inputId} mb={1}>
        {label}
      </FormLabel>
      <Box
        sx={{
          '.country-select__control': {
            minH: '40px',
            borderColor: 'gray.200'
          },
          '.country-select__control--is-focused': {
            borderColor: 'blue.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
          },
          '.country-select__menu': { zIndex: 20 },
          '.country-select__option': { textAlign: 'start' }
        }}
      >
        <Select<CountryOption, false>
          inputId={inputId}
          classNamePrefix="country-select"
          options={options}
          value={selectedOption}
          onChange={handleChange}
          onBlur={onBlur}
          isClearable
          isRtl={locale.toLocaleLowerCase().startsWith('ar')}
          placeholder={formatMessage({
            id: 'countrySelect.placeholder',
            defaultMessage: 'Select a country'
          })}
          noOptionsMessage={() =>
            formatMessage({
              id: 'countrySelect.noOptions',
              defaultMessage: 'No countries found'
            })
          }
        />
      </Box>
    </FormControl>
  )
}
